// UserRecharge.jsx
import React, { useState, useEffect } from "react";
import { HiSearch } from "react-icons/hi";
import { MdCancel } from "react-icons/md";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "@mui/material";
import { FaFilter, FaCalendarAlt, FaBolt, FaFileExcel } from "react-icons/fa";
import { GiMoneyStack } from "react-icons/gi";
import axios from "axios";
import Swal from "sweetalert2";
import ManualRechargeForm from "./ManualRechargeForm";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import exportToExcel from "utils/exportToExcel";
import { usePermission } from "Context/PermissionContext";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const UserRecharge = () => {
  const [combinedFilter, setCombinedFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [rechargeType, setRechargeType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.users?.subModule?.Recharge?.permissions;

  const loggedInUser = loadDecrypted("HeartConnect-StaffData");
  const staffData = Array.isArray(loggedInUser)
    ? loggedInUser[0]
    : loggedInUser;
  const staffId = staffData?._id || staffData?.id || "";
  const staffRole = staffData?.rolePermission?.rolename || "";

  // 🔹 Debounce search input (500ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const clearSearch = () => setSearchQuery("");

  // 🔹 Helper: Build API params based on filters
  const getParams = (isExport = false) => {
    const dateFilterMap = {
      today: "today",
      last7: "last7days",
      custom: "custom",
    };

    const params = {
      page: isExport ? undefined : page,
      limit: isExport ? undefined : limit,
      status: ["SUCCESS", "FAILED", "PENDING"].includes(combinedFilter)
        ? combinedFilter
        : undefined,
      paymentGateway: rechargeType !== "all" ? rechargeType : undefined,
      searchTerm: debouncedSearch || undefined,
      dateFilter: dateFilterMap[combinedFilter],
      startDate:
        combinedFilter === "custom" && customStartDate
          ? new Date(customStartDate.setHours(0, 0, 0, 0)).toISOString()
          : undefined,
      endDate:
        combinedFilter === "custom" && customEndDate
          ? new Date(customEndDate.setHours(23, 59, 59, 999)).toISOString()
          : undefined,
      ...(isExport && { exportAll: true }),
      requestedBy: staffId,
      requestedByRole: staffRole,
    };

    return params;
  };
  // 🔹 Fetch paginated transactions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/transaction-history/get-all-transaction-histories`,
        { params: getParams() }
      );
      setTransactions(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Export all transactions (only table data)
  const handleExportAll = async () => {
    try {
      Swal.fire({
        title: "Exporting...",
        text: "Please wait while we prepare your file.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/transaction-history/get-all-transaction-histories`,
        { params: getParams(true) }
      );

      const exportData = res.data;

      if (!exportData || exportData.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Data",
          text: "No transactions available to export.",
        });
        return;
      }

      const formatted = exportData.map((tx, index) => ({
        "Sl No.": index + 1,
        Date: new Date(tx.createdAt).toLocaleDateString(),
        Time: new Date(tx.createdAt).toLocaleTimeString(),
        User: tx.userID?.fullName || tx.userID?.nickName || "--",
        Phone: tx.userID?.phone ? tx.userID.phone.slice(2) : "--",
        "Amount (₹)": tx.amount || 0,
        "Payment Gateway": tx.paymentGateway?.toUpperCase() || "--",
        "Txn ID / UTR":
          tx.payment_gateway_details?.gateway_payment_id ||
          tx.payment_gateway_details?.utr ||
          tx.payment_gateway_details?.transactionId ||
          "--",
        "Recharged By": tx.payment_gateway_details?.rechargeByName || "--",

        Status: tx.status || "--",
      }));

      exportToExcel(formatted, "UserRechargeReport");

      Swal.fire({
        icon: "success",
        title: "Export Successful",
        text: "Your Excel file has been downloaded.",

        showConfirmButton: true,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "Unable to export full data.",
        showCloseButton: true,
        allowOutsideClick: false,
      });
    }
  };

  // 🔹 Fetch data whenever filters change
  useEffect(() => {
    setPage(1);
  }, [
    combinedFilter,
    rechargeType,
    customStartDate,
    customEndDate,
    debouncedSearch,
  ]);

  useEffect(() => {
    fetchTransactions();
  }, [
    combinedFilter,
    rechargeType,
    customStartDate,
    customEndDate,
    debouncedSearch,
    page,
  ]);

  return (
    <div className="py-5">
      {/* Recharge Button */}
      {currentTabPermissions?.create && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setOpenModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[#c51369] px-5 py-3 text-lg text-white hover:bg-[#a01052]"
          >
            <GiMoneyStack className="text-xl" />
            Recharge
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col items-center justify-between gap-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row">
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-center">
          {/* Combined Filter */}
          <div className="relative w-full md:w-44">
            <FaFilter className="absolute left-3 top-3 text-gray-500" />
            <select
              value={combinedFilter}
              onChange={(e) => setCombinedFilter(e.target.value)}
              className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
            >
              <option value="all">All Filter</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
              <option value="today">Today</option>
              <option value="last7">Last 7 Days</option>
              <option value="custom">Custom Date</option>
            </select>
          </div>

          {/* Custom Date Filters */}
          {combinedFilter === "custom" && (
            <>
              <div className="relative w-full md:w-40">
                <FaCalendarAlt className="absolute left-3 top-3 text-gray-500" />
                <DatePicker
                  selected={customStartDate}
                  onChange={(date) => setCustomStartDate(date)}
                  placeholderText="From"
                  dateFormat="dd/MM/yyyy"
                  className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                />
              </div>
              <div className="relative w-full md:w-40">
                <FaCalendarAlt className="absolute left-3 top-3 text-gray-500" />
                <DatePicker
                  selected={customEndDate}
                  onChange={(date) => setCustomEndDate(date)}
                  placeholderText="To"
                  dateFormat="dd/MM/yyyy"
                  className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                />
              </div>
            </>
          )}

          {/* Recharge Type */}
          <div className="relative w-full md:w-44">
            <FaBolt className="absolute left-3 top-3 text-gray-500" />
            <select
              value={rechargeType}
              onChange={(e) => setRechargeType(e.target.value)}
              className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
            >
              <option value="all">All Types</option>
              <option value="manual">Manual</option>
              <option value="cashfree">CashFree</option>
              <option value="cashfreefailed">Cashfree Failed</option>
            </select>
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-64">
            <HiSearch className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              placeholder="Search by phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="text-md absolute right-2 top-2 rounded px-2 py-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              >
                <MdCancel />
              </button>
            )}
          </div>
        </div>

        {/* Export Button */}
        {/* <div className="flex w-full justify-end md:w-auto">
          <button
            onClick={handleExportAll}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-[#c51369] px-5 py-2 text-white hover:bg-[#a01052]"
          >
            <FaFileExcel className="text-lg" /> Export
          </button>
        </div> */}
      </div>

      {/* Table */}
      <div className="mx-auto mt-5 w-auto rounded-t-2xl">
        <div className="overflow-x-auto rounded-t-2xl">
          {loading ? (
            <TableLoader columns={8} />
          ) : (
            <table className="w-full rounded-t-2xl text-left text-sm">
              <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                <tr className="text-center">
                  <th className="px-6 py-3">Sl No.</th>
                  <th className="px-6 py-3">Date & Time</th>
                  <th className="px-6 py-3">User</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">
                    Payments & <br /> Txn_id / UTR
                  </th>
                  <th className="px-6 py-3">Recharged By</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {transactions.length > 0 ? (
                  transactions.map((tx, index) => (
                    <tr
                      key={tx._id}
                      className="text-md bg-white tracking-wide text-gray-700"
                    >
                      <td className="border px-6 py-4">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td className="border px-6 py-4">
                        {new Date(tx.createdAt).toLocaleDateString()} <br />
                        {new Date(tx.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="border px-6 py-4">
                        <div className="flex flex-col items-center">
                          <span>
                            <span className="font-bold">Fullname: </span>
                            <span>{tx.userID?.fullName || "--"}</span>
                          </span>
                          <span>
                            <span className="font-bold">Nickname: </span>

                            <span>{tx.userID?.nickName || "--"}</span>
                          </span>
                          <span>
                            <span className="font-bold">Ph. number: </span>
                            <span>
                              {tx.userID?.phone
                                ? tx.userID.phone.slice(2)
                                : "--"}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="border px-6 py-4">₹{tx.amount}</td>
                      <td className="border px-6 py-4 text-center">
                        <div className="flex flex-col items-center">
                          <span>
                            {tx.paymentGateway?.toUpperCase() || "--"}
                          </span>
                          <span>
                            {tx.payment_gateway_details?.gateway_payment_id ||
                              tx.payment_gateway_details?.utr ||
                              tx.payment_gateway_details?.transactionId ||
                              "--"}
                          </span>
                        </div>
                      </td>

                      <td className="border px-6 py-4">
                        {tx.payment_gateway_details?.rechargeByName || "--"}
                      </td>
                      <td className="border px-6 py-4">
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${
                            tx.status === "SUCCESS"
                              ? "bg-green-100 text-green-800"
                              : tx.status === "FAILED"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {tx.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="py-4">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        showFirstLast
      />

      {/* Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <div className="flex h-screen w-screen items-center justify-center p-4">
          <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-lg">
            <ManualRechargeForm
              onClose={() => setOpenModal(false)}
              refetch={fetchTransactions}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserRecharge;

// import React, { useState } from "react";
// import { HiFilter, HiSearch } from "react-icons/hi";
// import { MdCancel } from "react-icons/md";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import { Modal } from "@mui/material";
// import { FaFilter, FaCalendarAlt, FaBolt } from "react-icons/fa";
// import { FaWallet } from "react-icons/fa";

// const UserRecharge = () => {

//   const [combinedFilter, setCombinedFilter] = useState("all");
//   const [customStartDate, setCustomStartDate] = useState(null);
//   const [customEndDate, setCustomEndDate] = useState(null);

//   const [rechargeType, setRechargeType] = useState("all");
//   const [searchQuery, setSearchQuery] = useState("");

//   const [openModal, setOpenModal] = useState(false);

//   // Dummy Data
//   // const dummyTransactions = [
//   //   {
//   //     _id: 1,
//   //     createdAt: new Date(),
//   //     user: { name: "Abhishek", avatar: "https://i.pravatar.cc/40" },
//   //     amount: 500,
//   //     rechargeType: "manual",
//   //     rechargedBy: "Admin",
//   //     status: "SUCCESS",
//   //   },
//   //   {
//   //     _id: 2,
//   //     createdAt: new Date(),
//   //     user: { name: "Riya", avatar: "https://i.pravatar.cc/41" },
//   //     amount: 300,
//   //     rechargeType: "cashfree",
//   //     rechargedBy: "Gateway",
//   //     status: "FAILED",
//   //   },
//   //   {
//   //     _id: 3,
//   //     createdAt: new Date(),
//   //     user: { name: "Amit", avatar: "https://i.pravatar.cc/42" },
//   //     amount: 200,
//   //     rechargeType: "manual",
//   //     rechargedBy: "Admin",
//   //     status: "PENDING",
//   //   },
//   // ];

//   const clearSearch = () => setSearchQuery("");

//   return (
//     <div className="py-5">
//       {/* Recharge Button */}

//       <div className="mb-4 flex justify-end ">
//         <button
//           onClick={() => {
//             setOpenModal(true);
//             console.log("modal is opened");
//           }}
//           className="flex items-center gap-2 rounded-lg bg-[#c51369] px-5 py-3 text-white hover:bg-[#a01052]"
//         >
//           <FaWallet className="text-lg" />
//           Manual Recharge
//         </button>
//       </div>

//       {/* Filters */}
//       <div className="flex flex-col items-center justify-start gap-2 space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
//         {/* Status + Date Filter in one */}
//         <div className="md:min-w-1/5 relative w-full md:w-1/5">
//           <FaFilter className="absolute left-3 top-3 text-gray-500" />
//           <select
//             value={combinedFilter}
//             onChange={(e) => setCombinedFilter(e.target.value)}
//             className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
//           >
//             <option value="all">All Filter</option>
//             <option value="SUCCESS">Success</option>
//             <option value="FAILED">Failed</option>
//             <option value="PENDING">Pending</option>
//             <option value="today">Today</option>
//             <option value="last7">Last 7 Days</option>
//             <option value="custom">Custom Date</option>
//           </select>
//         </div>

//         {/* Custom Date Pickers (only visible when "custom" selected) */}
//         {combinedFilter === "custom" && (
//           <>
//             <div className="relative w-full md:w-1/5">
//               <FaCalendarAlt className="absolute left-3 top-3 text-gray-500" />
//               <DatePicker
//                 selected={customStartDate}
//                 onChange={(date) => setCustomStartDate(date)}
//                 placeholderText="From Date"
//                 dateFormat="dd/MM/yyyy"
//                 className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
//               />
//             </div>
//             <div className="relative w-full md:w-1/5">
//               <FaCalendarAlt className="absolute left-3 top-3 text-gray-500" />
//               <DatePicker
//                 selected={customEndDate}
//                 onChange={(date) => setCustomEndDate(date)}
//                 placeholderText="To Date"
//                 dateFormat="dd/MM/yyyy"
//                 className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
//               />
//             </div>
//           </>
//         )}

//         {/* Recharge Type Filter */}
//         <div className="md:min-w-1/5 relative w-full md:w-1/5">
//           <FaBolt className="absolute left-3 top-3 text-gray-500" />
//           <select
//             value={rechargeType}
//             onChange={(e) => setRechargeType(e.target.value)}
//             className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
//           >
//             <option value="all">All Types</option>
//             <option value="manual">Manual</option>
//             <option value="cashfree">Cashfree</option>
//           </select>
//         </div>

//         {/* Search Input */}
//         <div className="md:min-w-2/5 relative w-full md:w-2/5">
//           <HiSearch className="absolute left-3 top-3 text-gray-500" />
//           <input
//             type="text"
//             placeholder="Search by phone..."
//             value={searchQuery}
//             onChange={(e) => setSearchQuery(e.target.value)}
//             className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
//           />
//           {searchQuery && (
//             <button
//               type="button"
//               onClick={clearSearch}
//               className="text-md absolute right-2 top-2 rounded px-2 py-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
//             >
//               <MdCancel />
//             </button>
//           )}
//         </div>
//       </div>

//       {/* Table */}
//       <div className="mx-auto mt-5 w-auto rounded-t-2xl">
//         <div className="overflow-x-auto rounded-t-2xl">
//           <table className="w-full rounded-t-2xl text-left text-sm">
//             <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
//               <tr className="text-center">
//                 <th className="px-6 py-3">Sl No.</th>
//                 <th className="px-6 py-3">Date & Time</th>
//                 <th className="px-6 py-3">User</th>
//                 <th className="px-6 py-3">Amount</th>
//                 <th className="px-6 py-3">Recharge Type</th>
//                 <th className="px-6 py-3">Recharged By</th>
//                 <th className="px-6 py-3">Status</th>
//               </tr>
//             </thead>
//             <tbody className="text-center">
//               {/* {dummyTransactions.map((transaction, index) => ( */}
//               <tr
//                 // key={transaction._id}
//                 className="text-md bg-white tracking-wide text-gray-700"
//               >
//                 <td className="border px-6 py-4">--</td>
//                 <td className="border px-6 py-4">
//                   <div className="flex flex-col items-center justify-center gap-1">
//                     <span>
//                       {/* {new Date(transaction.createdAt).toLocaleDateString()} */}
//                       --
//                     </span>
//                     <span>
//                       {/* {new Date(transaction.createdAt).toLocaleTimeString()} */}
//                       --
//                     </span>
//                   </div>
//                 </td>
//                 <td className="border px-6 py-4">
//                   <div className="flex flex-col items-center justify-center">
//                     {/* <img
//                         src={transaction.user.avatar}
//                         alt="avatar"
//                         className="h-10 w-10 rounded-full"
//                       /> */}
//                     {/* <span>{transaction.user.name}</span> */}
//                     ---
//                   </div>
//                 </td>
//                 <td className="border px-6 py-4">
//                   {/* ₹{transaction.amount} */}
//                   --
//                 </td>
//                 <td className="border px-6 py-4">
//                   {/* {transaction.rechargeType} */}
//                   --
//                 </td>
//                 <td className="border px-6 py-4">
//                   {/* {transaction.rechargedBy} */}
//                   --
//                 </td>
//                 <td className="border px-6 py-4">
//                   <span
//                   // className={`rounded-full px-2 py-1 text-xs ${
//                   //   transaction.status === "SUCCESS"
//                   //     ? "bg-green-100 text-green-800"
//                   //     : transaction.status === "FAILED"
//                   //     ? "bg-red-100 text-red-800"
//                   //     : "bg-yellow-100 text-yellow-800"
//                   // }`}
//                   >
//                     {/* {transaction.status} */}
//                     --
//                   </span>
//                 </td>
//               </tr>
//               {/* ))} */}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* Modal */}
//       <Modal open={openModal} onClose={() => setOpenModal(false)}>
//         <div className="absolute left-1/2 top-1/2 w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg">
//           <h2 className="mb-4 text-lg font-semibold">Recharge Modal</h2>
//           <p>Modal is opened (check console)</p>
//           <button
//             onClick={() => setOpenModal(false)}
//             className="mt-4 rounded bg-[#c51369] px-4 py-2 text-white"
//           >
//             Close
//           </button>
//         </div>
//       </Modal>
//     </div>
//   );
// };

// export default UserRecharge;
